import { useState } from 'react'
import { Button, EmptyNote, Icons, Loading, StateNote } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useJobs } from '@/lib/api/hooks'
import { PageFrame, Section } from '@/shell/PageFrame'
import { StudioOverview } from './StudioOverview'
import { JobCreateDialog } from '@/features/jobs/JobCreateDialog'
import { JobGroups } from '@/features/jobs/JobGroups'
import { JobQuickView } from '@/features/jobs/JobQuickView'
import type { Job } from '@workflow/contracts'
import { groupJobs, isOpenJob } from '@/features/jobs/jobPeriods'
import { AnnouncementBanner } from '@/features/announcements/AnnouncementsPage'

/**
 * The main page of the platform: the studio's overview at the top — the
 * month's hours, what is running, waiting and needing a hand, who is on the
 * clock — and the work itself beneath it, running first.
 */
export function HomePage() {
  const jobs = useJobs()
  const [adding, setAdding] = useState(false)
  const [viewing, setViewing] = useState<Job | null>(null)
  const viewed = viewing ? jobs.data?.find((job) => job.id === viewing.id) ?? viewing : null
  // Home is now: what is running, and the rest of this week's open work. Older
  // and finished work lives on the Jobs page, a month at a time.
  const open = (jobs.data ?? []).filter(isOpenJob)
  const running = open.filter((job) => job.status === 'in_progress')
  const thisWeek = groupJobs(open.filter((job) => job.status !== 'in_progress'), 'week').filter((group) => group.current)

  return (
    <PageFrame
      title="Home"
      description="What is running, what is waiting, and the month's hours."
      actions={
        <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setAdding(true)}>
          Add job
        </Button>
      }
    >
      <AnnouncementBanner />
      {jobs.data ? <StudioOverview jobs={jobs.data} onOpenJob={setViewing} /> : null}

      <Section title="Active Jobs">
        {jobs.isLoading ? (
          <Loading label="Loading the work" />
        ) : jobs.error ? (
          <StateNote
            title="The jobs did not load."
            detail={describeError(jobs.error)}
            action={
              <Button size="sm" icon={<Icons.retry />} onClick={() => void jobs.refetch()}>
                Try again
              </Button>
            }
          />
        ) : running.length === 0 && thisWeek.length === 0 ? (
          <EmptyNote
            title={(jobs.data?.length ?? 0) === 0 ? 'No jobs yet.' : 'Nothing running, and nothing new this week.'}
            detail={(jobs.data?.length ?? 0) === 0 ? 'Created jobs appear here.' : 'Earlier and finished work is on the Jobs page.'}
          />
        ) : (
          <JobGroups groups={thisWeek} lead={{ label: 'Running Now', jobs: running }} openAll />
        )}
      </Section>

      <JobQuickView job={viewed} onClose={() => setViewing(null)} />
      <JobCreateDialog open={adding} onClose={() => setAdding(false)} />
    </PageFrame>
  )
}
